/**
 * Ford Brand Theme System
 * Official Ford Motor Company brand colors, typography, and design tokens
 */

// Ford Brand Colors (Official Ford Design System)
export const FORD_COLORS = {
  // Primary Ford Blue
  primary: '#fff',           // Ford Blue
  primaryLight: '#1e4a8c',      // Lighter Ford Blue
  primaryDark: '#002654',       // Darker Ford Blue
  
  // Secondary Colors
  secondary: '#066fef',         // Ford Digital Blue
  accent: '#00b4d8',           // Ford Accent Blue
  
  // Neutral Colors
  background: '#f8f9fa',        // Light background
  // backgroundDark: 'hsla(220, 15%, 12%, 1.00)',    // Dark background
  backgroundDark: '#ffffff',    // Dark background
  surface: '#ffffff',           // White surface
  surfaceDark: '#2c3e50',      // Dark surface
  backgroundford: '#fafafa',
  
  // Text Colors
  textPrimary: '#212529',       // Primary text (dark)
  textSecondary: '#6c757d',     // Secondary text
  textLight: 'black',         // Light text
  textMuted: 'black',   
  textColorWhite: 'white',     // Muted text
  
  // Status Colors
  success: '#28a745',          // Success green
  warning: '#ffc107',          // Warning yellow
  error: '#dc3545',            // Error red
  info: '#17a2b8',             // Info blue
  
  // Ford Specific Colors
  fordOrange: '#ff6600',       // Ford Orange (accent)
  fordSilver: '#c0c0c0',       // Ford Silver
  fordCharcoal: '#36454f',     // Ford Charcoal
  backGroundColor: '#e5e7eb'
};

// Ford Typography System (Based on Ford Design Guidelines)
export const FORD_TYPOGRAPHY = {
  // Font Families
  fontFamily: '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fallbackFont: '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  monoFont: '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  newfont: '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  
  // Font Sizes (Ford Design System Scale)
  h1: '2.5rem',      // 40px - Page titles
  h2: '2rem',        // 32px - Section headers
  h3: '1.75rem',     // 28px - Subsection headers
  h4: '1.5rem',      // 24px - Component headers
  h5: '1.25rem',     // 20px - Small headers
  h6: '1.125rem',    // 18px - Labels
  body1: '1rem',     // 16px - Body text
  body2: '0.875rem', // 14px - Small body text
  caption: '0.75rem', // 12px - Captions, metadata
  overline: '0.625rem', // 10px - Overlines, tags
  
  // Font Weights (Ford Brand Guidelines)
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extraBold: 800,

  // Line Heights
  lineHeightTight: 1.2,
  lineHeightNormal: 1.5,
  lineHeightRelaxed: 1.75,
  
  // Letter Spacing
  letterSpacingTight: '-0.025em',
  letterSpacingNormal: '0',
  letterSpacingWide: '0.025em',
};

// Ford Spacing System (8px base unit)
export const FORD_SPACING = {
  xs: '4px',    // 0.25rem
  sm: '8px',    // 0.5rem
  md: '16px',   // 1rem
  lg: '24px',   // 1.5rem
  xl: '32px',   // 2rem
  xxl: '48px',  // 3rem
  xxxl: '64px', // 4rem
};

// Ford Border Radius
export const FORD_RADIUS = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

// Ford Shadows
export const FORD_SHADOWS = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  ford: '0 4px 12px rgba(0, 52, 120, 0.15)', // Ford Blue shadow
};

// Ford Component Styles
export const FORD_COMPONENTS = {
  // Button Styles
  button: {
    primary: {
      backgroundColor: FORD_COLORS.primary,
      color: FORD_COLORS.textLight,
      border: 'none',
      borderRadius: FORD_RADIUS.sm,
      padding: `${FORD_SPACING.md} ${FORD_SPACING.lg}`,
      fontSize: FORD_TYPOGRAPHY.body1,
      fontWeight: FORD_TYPOGRAPHY.medium,
      fontFamily: FORD_TYPOGRAPHY.fallbackFont,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: FORD_SHADOWS.sm,
      '&:hover': {
        backgroundColor: FORD_COLORS.primaryLight,
        transform: 'translateY(-1px)',
        boxShadow: FORD_SHADOWS.ford,
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: FORD_SHADOWS.sm,
      },
      '&:disabled': {
        backgroundColor: FORD_COLORS.textMuted,
        cursor: 'not-allowed',
        transform: 'none',
        boxShadow: 'none',
      },
    },
    secondary: {
      backgroundColor: FORD_COLORS.secondary,
      color: FORD_COLORS.textLight,
      border: 'none',
      borderRadius: FORD_RADIUS.sm,
      padding: `${FORD_SPACING.md} ${FORD_SPACING.lg}`,
      fontSize: FORD_TYPOGRAPHY.body1,
      fontWeight: FORD_TYPOGRAPHY.medium,
      fontFamily: FORD_TYPOGRAPHY.fallbackFont,
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      boxShadow: FORD_SHADOWS.sm,
      '&:hover': {
        backgroundColor: '#0554c7',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(6, 111, 239, 0.3)',
      },
    },
  },
  
  // Card Styles
  card: {
    backgroundColor: FORD_COLORS.surfaceDark,
    border: `1px solid ${FORD_COLORS.textMuted}40`,
    borderRadius: FORD_RADIUS.lg,
    padding: FORD_SPACING.lg,
    boxShadow: FORD_SHADOWS.md,
  },
  
  // Input Styles
  input: {
    backgroundColor: FORD_COLORS.surfaceDark,
    border: `1px solid ${FORD_COLORS.textMuted}60`,
    borderRadius: FORD_RADIUS.sm,
    padding: `${FORD_SPACING.md} ${FORD_SPACING.md}`,
    fontSize: FORD_TYPOGRAPHY.body1,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    color: FORD_COLORS.textLight,
    '&:focus': {
      borderColor: FORD_COLORS.secondary,
      outline: 'none',
      boxShadow: `0 0 0 2px ${FORD_COLORS.secondary}20`,
    },
  },
};

// Ford Breakpoints (Responsive Design)
export const FORD_BREAKPOINTS = {
  xs: '320px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px',
};

// Ford Animation Durations
export const FORD_ANIMATIONS = {
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  slower: '500ms',
};

// Export complete Ford theme object
export const FORD_THEME = {
  colors: FORD_COLORS,
  typography: FORD_TYPOGRAPHY,
  spacing: FORD_SPACING,
  radius: FORD_RADIUS,
  shadows: FORD_SHADOWS,
  components: FORD_COMPONENTS,
  breakpoints: FORD_BREAKPOINTS,
  animations: FORD_ANIMATIONS,
};

export default FORD_THEME;
